import { createSelector } from 'reselect';
import { getContactDataForNumber } from 'flow/selectors/contact-selectors';
import {
    ActiveCallDirections,
    convertNumberToE164,
    getRedirectNumber,
} from 'flow/utils/call-utils';
import {
    getNumberInRunUrlFriendlyFormat,
    getE164NumberInRunUrlFriendlyFormat,
    socialSecurityNumberToTwelveDigits,
    socialSecurityNumberToTenDigits,
    socialSecurityNumberToTwelveDigitsWithDash,
    socialSecurityNumberToTenDigitsWithDash,
    getExternalSearchWidgets,
    getVersion2Widgets,
} from 'flow/utils/run-url-utils';
import { parseMetadata } from 'flow/utils/call-widget-utils';
import { Map } from 'immutable';
import { getAgentInfo } from './call-selectors';
import { getOwnCountryCode } from './selectors';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { ActiveLineData } from 'flow/types/ActiveLineData';

export const getCallWidgetVariables = (
    state: State,
    activeCall: TypedMap<ActiveLineData>,
    queueNumber: string,
) => {
    let callInfo = Map();
    let bagVariables = Map();
    let contactInfo = Map();

    const loggedInUserCountryCode = getOwnCountryCode(state);

    if (activeCall) {
        callInfo = getCallInfo(
            state,
            activeCall,
            loggedInUserCountryCode,
            queueNumber,
        );
        bagVariables = activeCall.has('bagVariables')
            ? activeCall.get('bagVariables')
            : Map();
    }
    contactInfo =
        getContactInfo(
            state,
            convertNumberToE164(
                callInfo.get('counterpart') as string,
                loggedInUserCountryCode,
            ),
        ) || getContactInfo(state, callInfo.get('counterpart') as string);

    return callInfo.merge(bagVariables).set('CONTACT', contactInfo).toJS();
};

const getCallInfo = (
    state: State,
    activeCall: TypedMap<ActiveLineData>,
    loggedInUserCountryCode: string,
    queueNumber: string,
) => {
    let callInfo = Map();

    const source = getNumberInRunUrlFriendlyFormat(
        activeCall.get('source'),
        loggedInUserCountryCode,
    );
    const intSource = getE164NumberInRunUrlFriendlyFormat(
        activeCall.get('source'),
        loggedInUserCountryCode,
    );

    const destination = getNumberInRunUrlFriendlyFormat(
        activeCall.get('destination'),
        loggedInUserCountryCode,
    );
    const intDestination = getE164NumberInRunUrlFriendlyFormat(
        activeCall.get('destination'),
        loggedInUserCountryCode,
    );

    const redirectNumber = getNumberInRunUrlFriendlyFormat(
        getRedirectNumber(activeCall),
        loggedInUserCountryCode,
    );
    const cNumber =
        redirectNumber ||
        getNumberInRunUrlFriendlyFormat(queueNumber, loggedInUserCountryCode);
    const cIntNumber = getE164NumberInRunUrlFriendlyFormat(
        cNumber,
        loggedInUserCountryCode,
    );

    const counterpart =
        activeCall.get('direction') === ActiveCallDirections.INCOMING.value
            ? intSource
            : intDestination;

    /*
    caller, calle and from are lowercased to keep
    */
    callInfo = callInfo
        .set('NUMBER', source)
        .set('B-NUMBER', destination)
        .set('C-NUMBER', cNumber)
        .set('caller', intSource)
        .set('callee', intDestination)
        .set('from', cIntNumber)
        .set('counterpart', counterpart);

    const agentInfo = getAgentInfo(state, activeCall.get('callId'));
    const socialSecurityNumber = agentInfo && agentInfo.get('personalNumber');
    if (socialSecurityNumber) {
        const twelveDigitsWithNoDash =
            socialSecurityNumberToTwelveDigits(socialSecurityNumber);
        const twelveDigitsWithDash =
            socialSecurityNumberToTwelveDigitsWithDash(socialSecurityNumber);
        const tenDigitsWithNoDash =
            socialSecurityNumberToTenDigits(socialSecurityNumber);
        const tenDigitsWithDash =
            socialSecurityNumberToTenDigitsWithDash(socialSecurityNumber);
        callInfo = callInfo
            .set('SSNR', socialSecurityNumber)
            .set('SWE_SSNR_12_DIGITS_WITH_NO_DASH', twelveDigitsWithNoDash)
            .set('SWE_SSNR_12_DIGITS_WITH_DASH', twelveDigitsWithDash)
            .set('SWE_SSNR_10_DIGITS_WITH_NO_DASH', tenDigitsWithNoDash)
            .set('SWE_SSNR_10_DIGITS_WITH_DASH', tenDigitsWithDash);
    }

    return callInfo;
};

const getContactInfo = (state: State, remoteNumber: string) => {
    const contact = getContactDataForNumber(state, remoteNumber);
    return parseMetadata(contact);
};

export const getVersion1CallWidgets = (state: State) => {
    return getExternalSearchWidgets(state.callWidgets);
};

export const getVersion2CallWidgets = (state: State) => {
    return getVersion2Widgets(state.callWidgets);
};

export const selectHasCallWidgets = createSelector(
    (state: State) => state.callWidgets,
    (callWidgets) =>
        getExternalSearchWidgets(callWidgets)?.size > 0 ||
        getVersion2Widgets(callWidgets)?.size > 0,
);
